import { api } from "./base-config";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import { ETarResult, mapToETarResult } from "model/e-tar.result";
import {
  getPaymentMethodName,
  PaymentMethodEnum
} from "common/enums/PaymentMethodEnum";
import { Attestation } from "model/attestion.model";
import moment from "moment";
import { TarificationApplicationSession } from "model/tarification.model";
import { TarifiedItem } from "model/tarified-item.model";

/** ---- API CALLS ---- */
export const getTarificationApplicationSession = async (
  userId: number,
  stsToken: string,
  eHealthPassword: string
): Promise<TarificationApplicationSession> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/applicationSession`,
      {
        tenantId: "",
        keystorepw: eHealthPassword,
        rawtoken: stsToken,
        practiceStsToken: ""
      }
    )
    .then(response => mapToTarificationApplicationModel(response.data));
};

export const deleteTarificationSession = async (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number,
  tSession?: TarificationSessionKey
) => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/deleteHiddenContactTarificationSession`,
      {
        applicationSessionKey: session,
        tarificationSessionId: tSession && tSession.tarificationSessionId,
        patientId: patientId
      }
    )
    .then(response => response.data);
};

export const getTarifiedEAttestItemsToday = async (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number
): Promise<Array<TarifiedItem>> => {
  return (await getTarifiedItems(session, userId, patientId))
    .filter(item => moment().isSame(item.prestationDate, "d"))
    .filter(item => item.attestNr)
    .filter(item => item.attestNr.includes("-"));
};

export const getTarifiedItems = async (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number
): Promise<Array<TarifiedItem>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/tarifiedItems`,
      {
        applicationSessionKey: session,
        patientId: patientId
      }
    )
    .then(response => mapToTarifiedItems(response.data));
};

export const getReceipt = async (
  session: TarificationApplicationSession,
  tSession: TarificationSessionKey,
  userId: number
): Promise<string> => {
  return api
    .post(`/resources/secured/rs/caregivers/${userId}/tarifplus/receipt`, {
      applicationSessionKey: session,
      tarificationSessionId: tSession.tarificationSessionId
    })
    .then(response => (response.data ? response.data.pdf : ""));
};

export const getTarificationSession = async (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number,
  attestation: Attestation
): Promise<TarificationSessionKey> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/hiddenContactTarificationSession`,
      {
        applicationSessionKey: session,
        patientId: patientId,
        efact: attestation.eFact,
        eattest: attestation.eAttest,
        numberOfPatients: 1,
        nomenclaturesCodes: attestation.nomenclatures.map(n => n.code),
        skipRegisterContact: true,
        patientPaysContribution: attestation.coPayment,
        chronic: attestation.chronic,
        palliative: attestation.palliative
      }
    )
    .then(response => mapToTarificationSessionKey(response.data));
};

export const updateTarifiedItemAmount = async (
  session: TarificationApplicationSession,
  userId: number,
  tarificationSessionId: string,
  tarifiedItemId: string,
  amount: number
) => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/updateTarifiedItemAmount`,
      {
        applicationSessionKey: session,
        tarificationSessionId: tarificationSessionId,
        tarifiedItemId: tarifiedItemId,
        amount: Math.round(amount * 100) / 100
      }
    )
    .then(response => mapToTarificationSessionKey(response.data));
};

export const deleteTarifiedItem = async (
  session: TarificationApplicationSession,
  userId: number,
  tarificationSessionId: string,
  tarifiedItemId: string
) => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/deleteTarifiedItem`,
      {
        applicationSessionKey: session,
        tarificationSessionId: tarificationSessionId,
        tarifiedItemId: tarifiedItemId
      }
    )
    .then(response => response.statusText);
};

export const tarificationSessionDetails = async (
  session: TarificationApplicationSession,
  userId: number,
  tarificationSessionId: string
): Promise<TarificationSessionKey> => {
  return api
    .post(`/resources/secured/rs/caregivers/${userId}/tarifplus/details`, {
      applicationSessionKey: session,
      tarificationSessionId: tarificationSessionId
    })
    .then(response => mapToTarificationSessionKey(response.data));
};

export const checkETar = async (
  session: TarificationApplicationSession,
  tarificationSessionId: string,
  userId: number
): Promise<ETarResult> => {
  return api
    .post(`/resources/secured/rs/caregivers/${userId}/tarifplus/checkETar`, {
      applicationSessionKey: session,
      tarificationSessionId: tarificationSessionId
    })
    .then(response => mapToETarResult(response.data));
};

export const finishTarification = async (
  session: TarificationApplicationSession,
  tarificationSessionId: string,
  userId: number,
  payedAmount: number,
  paymentMethod: PaymentMethodEnum,
  eAttest: boolean
): Promise<string> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${userId}/tarifplus/finishTarificationSession`,
      {
        applicationSessionKey: session,
        tarificationSessionId: tarificationSessionId,
        payedAmount: payedAmount,
        paymentMethod: getPaymentMethodName(paymentMethod),
        eattest: eAttest
      }
    )
    .then(response => (response.data ? response.data.pdf : ""));
};

export const loadUserSettings = async (
  userId: number,
  session: TarificationApplicationSession
): Promise<boolean> => {
  return api
    .post(`/resources/secured/rs/caregivers/${userId}/tarifplus/userSettings`, {
      applicationSessionKey: session
    })
    .then(response => response.data)
    .then((dto: TarificationUserSettingsDTO) => dto.useEAttest ?? false);
};

/** ---- API MAPPING ---- */
const mapToTarificationApplicationModel = (
  dto: TarificationApplicationSessionDTO
): TarificationApplicationSession => {
  const session = new TarificationApplicationSession();
  session.advanced = dto.advanced;
  session.baseUrl = dto.baseUrl;
  session.expirationDate = moment(dto.expirationDate).toDate();
  session.eAttestAvailable = dto.eAttestAvailable;
  session.practice = dto.practice;
  session.sessionKey = dto.sessionKey;
  return session;
};

const mapToTarifiedItems = (
  items: Array<TarifiedItemDTO>
): Array<TarifiedItem> => {
  return items.map(it => mapToTarifiedItem(it));
};

const mapToTarifiedItem = (dto: TarifiedItemDTO): TarifiedItem => {
  const item = new TarifiedItem();
  item.id = dto.id || dto.Id || item.id;
  item.code = dto.code || dto.Code || item.code;
  item.honorarium = dto.honorarium || dto.Honorarium || item.honorarium;
  item.officialHonorarium =
    dto.officialHonorarium || dto.OfficialHonorarium || item.officialHonorarium;
  item.patientFee = dto.patientFee || dto.PatientFee || item.patientFee;
  item.prestationDate =
    (dto.prestationDate && moment(dto.prestationDate).toDate()) ||
    (dto.PrestationDate && moment(dto.PrestationDate).toDate()) ||
    item.prestationDate;
  item.reimbursement =
    dto.reimbursement || dto.Reimbursement || item.reimbursement;
  item.prestationCodeText =
    dto.prestationCodeToolTip ||
    dto.PrestationCodeToolTip ||
    item.prestationCodeText;
  item.attestNr = dto.attestNr || dto.AttestNr || item.attestNr;
  return item;
};

const mapToTarificationSessionKey = (
  dto: TarificationSessionKeyDTO
): TarificationSessionKey => {
  const session = new TarificationSessionKey();
  session.tarificationSessionId = dto.tarificationSessionId || dto.Id || "";
  session.edit = dto.edit || session.edit;
  session.totalPaid = dto.totalPaid || dto.TotalPaid || session.totalPaid;
  session.totalPatientContribution =
    dto.totalPatientContribution ||
    dto.TotalPatientContribution ||
    session.totalPatientContribution;
  session.totalReimbursed =
    dto.totalReimbursed || dto.TotalReimbursed || session.totalReimbursed;
  session.totalValue = dto.totalValue || dto.TotalValue || session.totalValue;
  session.warnings =
    (dto.warnings && dto.warnings.map(w => w.message || w.Message || "")) ||
    (dto.Warnings && dto.Warnings.map(w => w.message || w.Message || "")) ||
    [];
  session.errors =
    (dto.errors && dto.errors.map(w => w.message || w.Message || "")) ||
    (dto.Errors && dto.Errors.map(w => w.message || w.Message || "")) ||
    [];
  session.tarifiedItems =
    (dto.tarifiedItems && dto.tarifiedItems.map(it => mapToTarifiedItem(it))) ||
    (dto.TarifiedItems && dto.TarifiedItems.map(it => mapToTarifiedItem(it))) ||
    [];

  return session;
};

/** ---- API DTO ---- */
interface TarificationApplicationSessionDTO {
  readonly advanced: boolean;
  readonly baseUrl: string;
  readonly eAttestAvailable: boolean;
  readonly expirationDate: Date;
  readonly practice: string;
  readonly sessionKey: string;
}

interface TarificationUserSettingsDTO {
  readonly useEAttest?: boolean;
}

interface TarifiedItemDTO {
  readonly id?: string;
  readonly Id?: string;
  readonly code?: string;
  readonly Code?: string;
  readonly honorarium?: number;
  readonly Honorarium?: number;
  readonly officialHonorarium?: number;
  readonly OfficialHonorarium?: number;
  readonly patientFee?: number;
  readonly PatientFee?: number;
  readonly prestationDate?: string;
  readonly PrestationDate?: string;
  readonly prestationCodeToolTip?: string;
  readonly PrestationCodeToolTip?: string;
  readonly reimbursement?: number;
  readonly Reimbursement?: number;
  readonly AttestNr?: string;
  readonly attestNr?: string;
}

interface TarificationSessionKeyDTO {
  readonly tarificationSessionId?: string;
  readonly Id?: string;
  readonly edit?: boolean;
  readonly warnings?: TarificationSessionKeyWarningDTO[];
  readonly Warnings?: TarificationSessionKeyWarningDTO[];
  readonly errors?: TarificationSessionKeyErrorDTO[];
  readonly Errors?: TarificationSessionKeyErrorDTO[];
  readonly totalPaid?: number;
  readonly TotalPaid?: number;
  readonly totalPatientContribution?: number;
  readonly TotalPatientContribution?: number;
  readonly totalReimbursed?: number;
  readonly TotalReimbursed?: number;
  readonly totalValue?: number;
  readonly TotalValue?: number;
  readonly tarifiedItems?: TarifiedItemDTO[];
  readonly TarifiedItems?: TarifiedItemDTO[];
}

interface TarificationSessionKeyWarningDTO {
  readonly message?: string;
  readonly Message?: string;
}

interface TarificationSessionKeyErrorDTO {
  readonly message?: string;
  readonly Message?: string;
}
